import { algoliasearch } from 'algoliasearch'

import type { Gif } from '../types'
import { ExplicitRating } from '../../../../functions/src/shared/types/gif'

// Algolia configuration from environment variables
const ALGOLIA_APP_ID = import.meta.env.VITE_ALGOLIA_APP_ID
const ALGOLIA_SEARCH_API_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY
const ALGOLIA_INDEX_NAME = import.meta.env.VITE_ALGOLIA_INDEX_NAME

if (!ALGOLIA_APP_ID || !ALGOLIA_SEARCH_API_KEY || !ALGOLIA_INDEX_NAME) {
  throw new Error(
    'Missing Algolia configuration. Please check your environment variables: VITE_ALGOLIA_APP_ID, VITE_ALGOLIA_SEARCH_API_KEY, VITE_ALGOLIA_INDEX_NAME'
  )
}

// Initialize Algolia client with search-only API key (safe for frontend)
const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY)

/**
 * Search returns ONLY curator-approved (published) gifs — the unified pipeline's
 * publish gate. Algolia can't treat a *missing* `published` facet as true, so
 * pre-migration (before `migrate:unified` stamps `published:true` on the 369
 * approved docs) search returns zero results by design. The deploy runbook runs
 * the migration + reindex immediately after this code ships, so the window is
 * minutes. Kept behind a constant so it's a one-line flip if ever needed.
 */
const FILTER_UNPUBLISHED = true

export interface SearchOptions {
  hitsPerPage?: number
  page?: number
  ratingFilter?: ExplicitRating
}

export interface SearchResult {
  hits: Gif[]
  nbHits: number
  page: number
  nbPages: number
  hitsPerPage: number
  exhaustiveNbHits: boolean
  exhaustiveTypo: boolean
  exhaustive: boolean
  query: string
  params: string
  renderingContent: Record<string, unknown>
  processingTimeMS: number
}

interface AlgoliaHit {
  objectID: string
  [key: string]: unknown
}

/**
 * Transform Algolia search results into Gif objects
 * Algolia stores nested fields in flattened format (e.g., 'reference.source')
 * We need to reconstruct the nested structure for our Gif interface
 */
const transformAlgoliaHitToGif = (hit: AlgoliaHit): Gif => {
  // Reconstruct retrievalSearch from Algolia's flattened fields. Present only
  // for post-migration (approved) docs; left undefined otherwise so display
  // falls back to legacy fields via the gifDisplay* helpers.
  const narrative = hit['retrievalSearch.narrative'] as string | undefined
  const breadthTags = hit['retrievalSearch.breadthTags'] as string[] | undefined
  const scenarioExamples = hit['retrievalSearch.scenarioExamples'] as
    | string[]
    | undefined
  const distinctiveFeatures = hit['retrievalSearch.distinctiveFeatures'] as
    | string[]
    | undefined

  const hasRetrieval =
    narrative !== undefined ||
    breadthTags !== undefined ||
    scenarioExamples !== undefined

  return {
    id: hit.objectID,
    // Basic metadata - convert timestamps to Date objects
    createdAt: new Date(hit.createdAt as string),
    lastModified: new Date(hit.lastModified as string),
    durationSeconds: hit.durationSeconds as number,
    fileSizeBytes: (hit.fileSizeBytes as number) || 0,
    gifUrl: hit.gifUrl as string,
    // Reference object
    reference: {
      source: (hit['reference.source'] as string | null) || null,
      keywords: (hit['reference.keywords'] as string[]) || [],
      year: (hit['reference.year'] as number | null) || null,
    },
    explicitRating:
      (hit.explicitRating as ExplicitRating) || ExplicitRating.PG_13,
    // Denormalized retrieval prose (post-migration)
    retrievalSearch: hasRetrieval
      ? {
          narrative: narrative ?? '',
          scenarioExamples: scenarioExamples ?? [],
          breadthTags: breadthTags ?? [],
          distinctiveFeatures: distinctiveFeatures ?? [],
        }
      : undefined,
    publishedAt: hit.publishedAt as string | undefined,
    published: hit.published as boolean | undefined,
    // Legacy display fields no longer indexed — always undefined here.
    visualElements: undefined,
    emotionalProfile: undefined,
    useCases: undefined,
    // Popularity object
    popularity: {
      copies: (hit['popularity.copies'] as number) || 0,
      favorited: (hit['popularity.favorited'] as number) || 0,
    },
  }
}

export class SearchService {
  /**
   * Search for gifs using Algolia
   * @param query - The search query
   * @param options - Additional search options
   * @returns Promise<SearchResult> - The search results
   */
  static async searchGifs(
    query: string,
    options: SearchOptions = {}
  ): Promise<SearchResult> {
    try {
      const searchParams: Record<string, string | number> = {
        query,
        hitsPerPage: options.hitsPerPage ?? 20,
        page: options.page ?? 0,
      }

      // Build rating filter (use default if not provided)
      const filters = this.buildRatingFilters(
        options.ratingFilter || ExplicitRating.PG_13
      )
      // Only published (curator-approved) gifs are searchable.
      if (FILTER_UNPUBLISHED) {
        filters.push('published:true')
      }
      if (filters.length > 0) {
        searchParams.filters = filters.join(' AND ')
      }

      const response = await client.searchSingleIndex({
        indexName: ALGOLIA_INDEX_NAME,
        searchParams,
      })

      // Transform Algolia hits directly to Gif objects
      const gifs = response.hits.map((hit) =>
        transformAlgoliaHitToGif(hit as AlgoliaHit)
      )

      return {
        hits: gifs,
        nbHits: response.nbHits ?? 0,
        page: response.page ?? 0,
        nbPages: response.nbPages ?? 0,
        hitsPerPage: response.hitsPerPage ?? 20,
        exhaustiveNbHits: response.exhaustiveNbHits ?? false,
        exhaustiveTypo: response.exhaustiveTypo ?? false,
        exhaustive: Boolean(response.exhaustive),
        query: response.query ?? '',
        params: response.params ?? '',
        renderingContent: response.renderingContent ?? {},
        processingTimeMS: response.processingTimeMS ?? 0,
      }
    } catch (error) {
      console.error('Error searching gifs:', error)
      throw new Error(
        `Failed to search gifs: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Get search suggestions based on a partial query
   * @param query - The partial query
   * @returns Promise<string[]> - Array of suggested queries
   */
  static async getSuggestions(query: string): Promise<string[]> {
    try {
      if (!query || query.length < 2) {
        return []
      }

      const response = await client.searchSingleIndex({
        indexName: ALGOLIA_INDEX_NAME,
        searchParams: {
          query,
          hitsPerPage: 5,
          attributesToRetrieve: [
            'reference.keywords',
            'retrievalSearch.breadthTags',
            // Optional legacy fallback for pre-migration docs still indexed.
            'visualElements.visualTags',
          ],
        },
      })

      // Extract unique keywords from the results for suggestions
      const suggestions = new Set<string>()

      const addMatching = (tags: string[] | undefined) => {
        tags?.forEach((tag) => {
          if (tag.toLowerCase().includes(query.toLowerCase())) {
            suggestions.add(tag)
          }
        })
      }

      response.hits.forEach((hit) => {
        const algoliaHit = hit as unknown as Record<string, unknown>

        // Reference keywords (flattened format from Algolia)
        addMatching(algoliaHit['reference.keywords'] as string[] | undefined)

        // Breadth tags from the retrieval record (post-migration)
        addMatching(
          algoliaHit['retrievalSearch.breadthTags'] as string[] | undefined
        )

        // Legacy visual tags — optional fallback for pre-migration docs.
        addMatching(
          algoliaHit['visualElements.visualTags'] as string[] | undefined
        )
      })

      return Array.from(suggestions).slice(0, 5)
    } catch (error) {
      console.error('Error getting suggestions:', error)
      return []
    }
  }

  /**
   * Build rating filters for Algolia search based on selected maximum rating
   * @param maxRating - Maximum allowed rating (includes all ratings up to this level)
   * @returns string[] - Array of filter strings for Algolia
   */
  private static buildRatingFilters(maxRating: ExplicitRating): string[] {
    const filters: string[] = []

    // Define rating hierarchy (from most restrictive to least)
    const ratingHierarchy: ExplicitRating[] = [
      ExplicitRating.G,
      ExplicitRating.PG,
      ExplicitRating.PG_13,
      ExplicitRating.R,
    ]
    const maxRatingIndex = ratingHierarchy.indexOf(maxRating)

    if (maxRatingIndex === -1) {
      // If rating not found, default to all ratings
      return []
    }

    // Include all ratings up to and including the selected rating
    const allowedRatings = ratingHierarchy.slice(0, maxRatingIndex + 1)
    const ratingFilters = allowedRatings.map(
      (rating) => `explicitRating:${rating}`
    )

    if (ratingFilters.length > 0) {
      filters.push(`(${ratingFilters.join(' OR ')})`)
    }

    return filters
  }
}
